// supervisor-run-until-idle.ts
//
// Drives `runOneSupervisorTick` repeatedly until the DAG is exhausted
// (`canAdvancePhase` true for every ticket), a ticket is blocked/escalated,
// or a pause/abort directive is observed. Phase 06 (TD1 [HD1]): quota sleep
// or a §18 guard (quiet_hours, pin_drift, …) that fires MID-RUN no longer
// polls-and-waits inline to maxTicks — the loop returns immediately with
// outcome "quota_sleeping"/"before_dispatch_blocked" and the wake/resume
// time, and the operator re-runs `avatar autopilot run` after that point.
// A future real daemon that DOES want to wait inline is a separate module.
import { createContractErrorBreaker } from "./contract-error-circuit-breaker.js";
import { ensureG2DecisionRecorded } from "./g2-dispatch-gate-enforcement.js";
import type { G2PlanReadiness } from "./g2-plan-readiness.js";
import { noFeatureTicketHaltReason } from "./plan-done-feature-ticket-guards.js";
import { clearQuotaSleepState, isAwake, readQuotaSleepState } from "./quota-sleep-scheduler.js";
import { canAdvancePhase } from "./scheduler.js";
import {
  checkBeforeDispatchGate,
  mapBeforeDispatchGateVerdictToResult,
} from "./supervisor-before-dispatch-gate-step.js";
import { runCandidateReviewPhase } from "./supervisor-candidate-review-phase.js";
import { applyHaltDirectivesAtTickBoundary } from "./supervisor-halt-directive-step.js";
import { runOneSupervisorTick } from "./supervisor-loop.js";
import { emitRunHeaderProgressLine } from "./supervisor-run-header-progress-line.js";
import {
  describeTickBudgetExhaustion,
  mapTickResultToLoopAction,
} from "./supervisor-run-until-idle-tick-outcome.js";
import type { RunUntilIdleInput, RunUntilIdleResult } from "./supervisor-run-until-idle-types.js";
import { drainTickBoundaryDirectives } from "./supervisor-tick-boundary-directive-drain.js";
import { buildSchedulerProjection, loadTicketsFromRepo } from "./supervisor-ticket-projection.js";
import { runTransportTick } from "./supervisor-transport-tick.js";

// Types live in supervisor-run-until-idle-types.ts (split, plan 20260924-0205);
// re-exported so `import … from "./supervisor-run-until-idle.js"` keeps working.
export type {
  BeforeDispatchDeps,
  G2DecisionDeps,
  RunUntilIdleInput,
  RunUntilIdleOutcome,
  RunUntilIdleResult,
} from "./supervisor-run-until-idle-types.js";

const DEFAULT_MAX_TICKS = 10_000;

/**
 * Runs ticks until the mission has nothing left to do. Every iteration
 * re-reads tickets from disk (proposal-applier / retry-policy splits may
 * have added new ones) and re-derives the scheduler projection from the
 * store's CURRENT state — never a stale snapshot from a previous tick.
 */
export async function runSupervisorUntilIdle(
  input: RunUntilIdleInput,
): Promise<RunUntilIdleResult> {
  const maxTicks = input.maxTicks ?? DEFAULT_MAX_TICKS;
  const quotaStatePath = `${input.controlRoot}/quota-sleep.json`;
  // P25: set once G2 is evaluated, then attached to whichever return path
  // this run takes. Declared out here on purpose — the bug being closed is a
  // value computed in the loop and dropped by the returns.
  let g2UnenforcedConditions: readonly string[] | undefined;

  // Phase 04 (R-23, D11, B21): ONE breaker instance for this entire `run` —
  // production (supervisor-mission-run.ts) never passes one, so it is
  // created here. Used for BOTH dispatch paths below (feature ticket AND
  // review candidate) so a contract_error on either counts toward the same
  // halt — finalizeAttemptAfterRoute reads it off tickInput for whichever
  // path dispatched.
  const loopInput = {
    ...input,
    contractErrorBreaker: input.contractErrorBreaker ?? createContractErrorBreaker(),
    // 06g R5: the ▶ line reads the ceiling the §18 gate enforces (same config).
    costLimitUsd: input.costLimitUsd ?? input.beforeDispatch?.config.max_cost_usd_per_mission,
  };

  await emitRunHeaderProgressLine(input);

  for (let i = 0; i < maxTicks; i++) {
    // Non-blocking transport step (spec §7: outbox drain + directive poll)
    // — local-only mode when input.mattermostTransport is undefined, per
    // supervisor-transport-tick.ts's own contract.
    await runTransportTick({
      controlRoot: input.controlRoot,
      machineLockPath: input.machineLockPath ?? `${input.controlRoot}/machine.lock`,
      missionId: input.missionId,
      mattermost: input.mattermostTransport,
    });

    const halt = await applyHaltDirectivesAtTickBoundary(input);
    if (halt) return { outcome: halt, ticksRun: i, g2UnenforcedConditions };

    const quota = await readQuotaSleepState(quotaStatePath);
    if (quota !== undefined) {
      if (!isAwake(quota, (input.now ?? (() => new Date()))())) {
        return {
          outcome: "quota_sleeping",
          ticksRun: i,
          wakeAt: quota.wake_at,
          g2UnenforcedConditions,
        };
      }
      await clearQuotaSleepState(quotaStatePath); // 06h R5a: woke up ⇒ the file has served its purpose.
    }

    // Tick boundary: approve → answer → redirect drains, in that order, before
    // the dispatch decision — see supervisor-tick-boundary-directive-drain.ts.
    await drainTickBoundaryDirectives(input);

    // Phase 04 (R-23, D11): checked BEFORE either dispatch path — a
    // contract_error recorded on the PREVIOUS tick (feature or review) can
    // halt the NEXT tick's dispatch outright, same rationale as the quota/
    // gate checks above (terminal for this `run`, not a spin to maxTicks).
    const breakerHalt = loopInput.contractErrorBreaker.haltReason();
    if (breakerHalt) {
      return { outcome: "blocked", ticksRun: i, haltReason: breakerHalt, g2UnenforcedConditions };
    }

    const tickets = await loadTicketsFromRepo(input.repoDir);
    const projection = buildSchedulerProjection(tickets, input.store.state.tickets);

    if (canAdvancePhase(tickets, projection.ticketStatus) && tickets.length > 0) {
      // DAG exhausted ⇒ candidate review (O-1/NEW-1: runCandidateReviewPhase) unless no feature (F-1 R3).
      const noFeature = noFeatureTicketHaltReason(tickets, projection.ticketStatus);
      if (noFeature)
        return { outcome: "blocked", ticksRun: i, haltReason: noFeature, g2UnenforcedConditions };
      const reviewResult = await runCandidateReviewPhase(loopInput, i, g2UnenforcedConditions);
      if (reviewResult) return reviewResult;
      continue;
    }

    // G2 (P16, H4): AFTER the tickets are loaded and BEFORE dispatch. Nothing
    // is requested until the plan is ready; the request is bound to the plan
    // content hash and re-issued when the content changes (never carried
    // over). The readiness goes to the guard, which holds features meanwhile.
    // Only when the caller supplied the config-derived inputs.
    let g2PlanReadiness: G2PlanReadiness | undefined;
    if (input.g2Decision) {
      const g2Result = await ensureG2DecisionRecorded({
        controlRoot: input.controlRoot,
        repoDir: input.repoDir,
        missionId: input.missionId,
        tickets,
        ticketStatus: projection.ticketStatus,
        g2TicketThreshold: input.g2Decision.g2TicketThreshold,
        g0Delegated: input.g2Decision.g0Delegated,
        // P25: lets the gate journal any §8 condition it could not measure.
        store: input.store,
        // H4 D5: the journal ledger of controller-made redirect decision tickets.
        missionState: input.store.state,
        now: input.now,
      });
      g2PlanReadiness = g2Result.planReadiness;
      // P25: remember it for EVERY return path below. Assigning it to a field
      // the returns then ignore is precisely the bug this phase is closing.
      g2UnenforcedConditions = g2Result.unenforcedConditions;
    }

    // §18 gate — see supervisor-before-dispatch-gate-step.ts. A cost/attempt
    // ceiling breach is terminal for this run; every other block exits early
    // with a reason (TD1) instead of spinning to maxTicks.
    const gate = await checkBeforeDispatchGate(input);
    const gateResult = mapBeforeDispatchGateVerdictToResult(gate, i, g2UnenforcedConditions);
    if (gateResult) return gateResult;

    const result = await runOneSupervisorTick({ ...loopInput, projection, g2PlanReadiness });
    // Terminal-return-or-continue mapping split to supervisor-run-until-idle-
    // tick-outcome.ts (phase 03, line budget) — same behavior, moved verbatim.
    const action = await mapTickResultToLoopAction({
      result,
      tickets,
      projection,
      tickIndex: i,
      g2UnenforcedConditions,
      quotaStatePath,
      missionId: input.missionId,
    });
    if (action.kind === "return") return action.result;
  }

  // Fell off the tick budget: `idle` only if nothing is left — otherwise say
  // so (re-review 2026-09-26, A-c). Re-read: the last tick may have added tickets.
  const leftover = await loadTicketsFromRepo(input.repoDir);
  const leftoverStatus = buildSchedulerProjection(leftover, input.store.state.tickets).ticketStatus;
  return {
    outcome: "idle",
    ticksRun: maxTicks,
    g2UnenforcedConditions,
    ...describeTickBudgetExhaustion(leftover, leftoverStatus),
  };
}
